/*
 * The garage. One page for every category, driven by the registry, so axles or
 * sprockets appear here the moment they exist in Python.
 *
 * Adding a part is a drawer rather than a separate page, because it is almost
 * always reached mid-setup ("this rim isn't in the list") and navigating away
 * would lose whatever was half-entered on the setup sheet.
 *
 * Editing uses that same drawer. A part gets entered in a hurry at a track with
 * half its dimensions missing and the correction turns up later, so entering one
 * and fixing one have to be the same job rather than a re-entry. Nothing in the
 * form is per-category - it is built from the registry - so every category that
 * exists now or arrives later is editable for free.
 *
 * Parts are retired, never deleted: sessions that ran a part still point at it
 * and their history would break. Retiring only hides it from the setup-sheet
 * dropdowns, and can be undone here.
 */

import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, RotateCcw } from 'lucide-react'
import { ApiError, api } from '../lib/api'
import type { CategoryDef, GaragePart } from '../lib/api'
import { Empty, Spinner } from '../components/bits'

export function Garage() {
  const { category = 'rims' } = useParams()
  const qc = useQueryClient()

  // null = closed, 'new' = adding, a part = editing that part.
  const [open, setOpen] = useState<'new' | GaragePart | null>(null)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [name, setName] = useState('')
  const [position, setPosition] = useState('')
  const [retired, setRetired] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showRetired, setShowRetired] = useState(false)

  const editing = open !== null && open !== 'new' ? open : null

  function openNew() {
    setOpen('new')
    setName(''); setPosition(''); setDraft({}); setRetired(false); setError(null)
  }

  function openEdit(p: GaragePart) {
    setOpen(p)
    setName(p.name)
    setPosition(p.position)
    // A copy, so cancelling leaves the part on screen untouched.
    setDraft({ ...p.specs })
    setRetired(p.retired)
    setError(null)
  }

  function close() {
    setOpen(null); setError(null)
  }

  const cats = useQuery({
    queryKey: ['garage'],
    queryFn: () => api.get<{ categories: CategoryDef[] }>('/api/garage'),
  })
  const list = useQuery({
    queryKey: ['garage', category, showRetired],
    queryFn: () => api.get<{ parts: GaragePart[] }>(
      `/api/garage/${category}${showRetired ? '?include_retired=true' : ''}`),
  })

  const save = useMutation({
    mutationFn: () => {
      const body = { name, position, specs: draft, retired }
      return editing
        ? api.patch(`/api/garage/${category}/${editing.id}`, body)
        : api.post(`/api/garage/${category}`, body)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['garage'] })
      close()
    },
    onError: (e: unknown) =>
      setError(e instanceof ApiError ? e.message : 'Could not save.'),
  })

  // Putting one back is the same PATCH with retired cleared, so it needs no drawer.
  const restore = useMutation({
    mutationFn: (p: GaragePart) => api.patch(`/api/garage/${category}/${p.id}`, {
      name: p.name, position: p.position, specs: p.specs, retired: false,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['garage'] }),
  })

  if (cats.isLoading || list.isLoading) return <div className="page"><Spinner /></div>

  const def = cats.data?.categories.find((c) => c.key === category)
  if (!def) return <div className="page"><Empty title="Unknown category" body="" /></div>
  const parts = list.data?.parts ?? []
  const noun = def.label.toLowerCase()

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>{def.plural}</h1>
          <div className="sub">Your kart equipment. Entered once, then picked
            from a dropdown on every setup sheet.</div>
        </div>
        <button className="btn" onClick={() => (open === 'new' ? close() : openNew())}>
          <Plus size={16} /> Add {noun}
        </button>
      </div>

      {open !== null && (
        <div className="card" style={{ marginBottom: 14 }}>
          <h2 style={{ marginBottom: 12 }}>
            {editing ? `Edit ${editing.name}` : `New ${noun}`}
          </h2>
          <label className="field">
            <span className="name">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)}
              placeholder={category === 'rims' ? 'MXC 130 Front' : ''} />
          </label>
          {def.positioned && (
            <label className="field">
              <span className="name">Fits</span>
              <select value={position} onChange={(e) => setPosition(e.target.value)}>
                <option value=""></option>
                <option value="front">front</option>
                <option value="rear">rear</option>
              </select>
            </label>
          )}
          {def.specs.map((s) => (
            <label className="field" key={s.key}>
              <span className="name">{s.label}{s.unit ? ` (${s.unit})` : ''}</span>
              {s.options.length ? (
                <select value={draft[s.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}>
                  <option value=""></option>
                  {s.options.map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={s.kind === 'num' ? 'number' : 'text'} step="any"
                  value={draft[s.key] ?? ''}
                  onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))} />
              )}
              {s.hint && <span className="hint">{s.hint}</span>}
            </label>
          ))}

          {editing && (
            <label className="field">
              <span className="name">Retired</span>
              <span className="row" style={{ gap: 8, alignItems: 'center' }}>
                <input type="checkbox" checked={retired} style={{ width: 'auto' }}
                  onChange={(e) => setRetired(e.target.checked)} />
                <span className="hint" style={{ margin: 0 }}>
                  Hides it from setup sheets. Sessions that ran it keep it.
                </span>
              </span>
            </label>
          )}

          {error && <div className="advice warn">{error}</div>}
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn" disabled={!name || save.isPending}
              onClick={() => save.mutate()}>
              {save.isPending ? 'Saving…' : editing ? 'Save changes' : `Save ${noun}`}
            </button>
            <button className="btn ghost" onClick={close}>Cancel</button>
          </div>
        </div>
      )}

      {parts.length === 0 ? (
        <Empty title={`No ${def.plural.toLowerCase()} in your garage`}
          body={`Add your kart ${def.plural.toLowerCase()} once and they become available on every setup sheet.`}
          action={<button className="btn" onClick={openNew}>
            <Plus size={16} /> Add {noun}</button>} />
      ) : (
        <div className="grid three-col">
          {parts.map((p) => (
            <div className="card" key={p.id}
              style={p.retired ? { opacity: 0.6 } : undefined}>
              <div className="between">
                <h3>{p.name}</h3>
                <span className="row" style={{ gap: 6 }}>
                  {p.position && <span className="badge">{p.position}</span>}
                  {p.retired && <span className="badge">retired</span>}
                </span>
              </div>
              <div className="small muted" style={{ marginTop: 8 }}>
                {def.specs
                  .filter((s) => s.key !== 'notes' && p.specs[s.key])
                  .map((s) => `${p.specs[s.key]}${s.unit ? ' ' + s.unit : ''}`)
                  .join(' · ') || 'No dimensions recorded'}
              </div>
              <div className="row" style={{ gap: 8, marginTop: 10 }}>
                <button className="btn ghost small" onClick={() => openEdit(p)}>
                  <Pencil size={13} /> Edit
                </button>
                {p.retired && (
                  <button className="btn ghost small" disabled={restore.isPending}
                    onClick={() => restore.mutate(p)}>
                    <RotateCcw size={13} /> Put back
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <label className="row" style={{ gap: 8, marginTop: 16, alignItems: 'center' }}>
        <input type="checkbox" checked={showRetired} style={{ width: 'auto' }}
          onChange={(e) => setShowRetired(e.target.checked)} />
        <span className="small muted">Show retired {def.plural.toLowerCase()}</span>
      </label>
    </div>
  )
}
